import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PropertyFilters from "@/components/PropertyFilters";
import PropertyListingCard from "@/components/PropertyListingCard";
import { useLocations } from "@/hooks/useLocations";
import { useProperties } from "@/hooks/useProperties";
import {
  applyPropertySearchFilters,
  defaultPropertySearchFilters,
  filtersFromSearchParams,
  filtersToSearchParams,
} from "@/lib/propertyFilters";

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const { data: properties, isLoading } = useProperties();
  const { data: locations = [], isLoading: loadingLocations } = useLocations();
  const filteredProperties = useMemo(() => applyPropertySearchFilters(properties ?? [], filters), [properties, filters]);

  return (
    <div className="bg-brand-canvas min-h-screen">
      <Header />
      <main className="pt-32 md:pt-36 w-full">
        <div className="px-4 md:px-16 py-6">
          <nav className="flex items-center gap-2 text-sm text-primary-foreground/70 flex-wrap">
            <a href="/" className="hover:text-primary-foreground transition-colors">Home</a>
            <span>/</span><span>Search Results</span>
          </nav>
        </div>

        <div className="px-4 md:px-16 py-8 md:py-12">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="font-display text-3xl md:text-5xl font-light text-primary-foreground mb-4 md:mb-6">Property Search Results</h1>
            <p className="font-body text-base md:text-lg text-primary-foreground/75 max-w-3xl mx-auto px-4">
              Browse properties that match the filters selected from the homepage search.
            </p>
          </div>

          <PropertyFilters
            value={filters}
            locations={locations}
            loadingLocations={loadingLocations}
            onChange={(next) => setSearchParams(filtersToSearchParams(next))}
            onReset={() => setSearchParams(filtersToSearchParams(defaultPropertySearchFilters))}
            className="max-w-7xl mx-auto"
          />
        </div>

        <div className="px-4 md:px-16 pb-16">
          <div className="max-w-7xl mx-auto">
            <p className="font-body text-sm text-primary-foreground/75 mb-6">
              Showing <span className="text-primary-foreground font-semibold">{filteredProperties.length}</span> matching properties.
            </p>

            {isLoading ? (
              <p className="text-center text-primary-foreground/70 font-body py-12">Loading properties...</p>
            ) : filteredProperties.length > 0 ? (
              <div className="grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3">
                {filteredProperties.map((property) => (
                  <PropertyListingCard key={property.id} {...property} statusLabel={property.status_label} categoryLabel={property.category_label} />
                ))}
              </div>
            ) : (
              <p className="text-center text-primary-foreground/70 font-body py-12">No properties match the selected filters yet.</p>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SearchResultsPage;